import type { WhoamiResponse } from "@/types/whoamiTypes";

/** The user as `/api/whoami` sends it: PropelAuth's own `User`. */
export type WhoamiUser = NonNullable<WhoamiResponse["user"]>;

/**
 * Three outcomes rather than `user | null`, because the caller has to tell an
 * expired token (ask the parent for a new one) apart from the API being
 * unreachable (retrying would just spin).
 */
export type WhoamiResult =
  | { status: "ok"; user: WhoamiUser }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

/**
 * Resolves an access token into the identity behind it.
 *
 * Deliberately does not extend or use `Client`. `Client.getHeaders()` reads the
 * token out of `GlobalStore`, and the whole point of this call is to turn a token
 * we were just handed into the user it belongs to *before* anything is in the
 * store -- going through `Client` would throw "No access token available".
 */
export class WhoamiController {
  async fetch(accessToken: string): Promise<WhoamiResult> {
    let response: Response;
    try {
      response = await fetch("/api/whoami", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      return { status: "error", message: (error as Error).message };
    }

    if (response.status === 401 || response.status === 403) {
      return { status: "unauthorized" };
    }
    if (!response.ok) {
      return { status: "error", message: `/api/whoami responded ${response.status}` };
    }

    try {
      const body: WhoamiResponse = await response.json();
      if (!body?.user) {
        return { status: "error", message: "/api/whoami returned no user" };
      }
      return { status: "ok", user: body.user };
    } catch (error) {
      return { status: "error", message: (error as Error).message };
    }
  }
}
